<template>
  <div class="dlna-device-selector">
    <!-- DLNA设备选择按钮 -->
    <div class="dropdown dropdown-hover dropdown-end">
      <div tabindex="0" role="button" class="btn btn-sm btn-ghost flex">
        <svg-icon name="listen" icon-class="w-4 h-4" />
        <span v-if="!selectedDevice" class="!hidden md:!block">{{ $tt('本机播放') }}</span>
        <span v-else class="text-neutral !hidden md:!block">{{ selectedDevice.device_info.name }}</span>
        <svg class="w-3 h-3 ml-1 fill-current !hidden md:!block" viewBox="0 0 1024 1024">
          <path
            d="M512 714.666667L213.333333 416l42.666667-42.666667L512 629.333333l256-256L810.666667 416 512 714.666667z" />
        </svg>
      </div>
      <ul tabindex="0" class="dropdown-content z-[2] menu p-2 shadow bg-base-100 rounded-box w-64"
        :style="{ 'max-height': deviceListHeight }">
        <!-- 本机播放选项 -->
        <li>
          <a @click="selectDevice(null)" :class="{ 'active': !selectedDevice }">
            <svg class="w-4 h-4 fill-current" viewBox="0 0 1024 1024">
              <path
                d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64z m0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
            </svg>
            <div>{{ $tt('本机播放') }}</div>
          </a>
        </li>
        <div class="divider my-0"></div>

        <!-- 刷新设备按钮 -->
        <li>
          <a @click="refreshDevices(true)">
            <svg t="1734162100000" class="w-4 h-4 fill-current" :class="{ 'animate-spin': isRefreshing }"
              viewBox="0 0 1024 1024">
              <path
                d="M909.1 209.3l-56.4 44.1C775.8 155.1 656.2 92 521.9 92 290 92 102.3 279.5 102 511.5 101.7 743.7 289.8 932 521.9 932c181.2 0 335.8-115.7 394.9-277.7 6.1-16.8-2.8-34.9-19.6-41-16.8-6.1-34.9 2.8-41 19.6-45.4 124.8-165.6 213.1-334.3 213.1-178.4 0-323.9-145.3-323.6-323.9C198.6 344.7 344.2 200 521.9 200c123.5 0 230.8 69.6 284.9 171.4l-163.7 40.7-17.3 4.3c-17.8 4.4-28.6 22.4-24.2 40.2 4.4 17.8 22.4 28.6 40.2 24.2l17.3-4.3 235.1-58.4c17.8-4.4 28.6-22.4 24.2-40.2l-58.4-235.1c-4.4-17.8-22.4-28.6-40.2-24.2-17.8 4.4-28.6 22.4-24.2 40.2l9.1 36.6z" />
            </svg>
            {{ isRefreshing ? $tt('搜索中...') : $tt('刷新设备') }}
          </a>
        </li>

        <!-- 手动添加设备按钮 -->
        <li>
          <a @click="showAddDeviceModal = true">
            <svg class="w-4 h-4 fill-current" viewBox="0 0 1024 1024">
              <path
                d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64z m192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z" />
            </svg>
            {{ $tt('手动添加设备') }}
          </a>
        </li>

        <div class="divider my-0"></div>

        <!-- 设备列表区域 -->
        <div v-if="devices.length === 0 && !isRefreshing" class="px-4 py-2 text-gray-500 text-sm text-center">
          {{ $tt('未发现DLNA设备') }}
        </div>

        <!-- 设备过多时的提示 -->
        <div v-if="showDeviceCount" class="px-4 py-1 text-xs text-gray-400 text-center border-b border-gray-200 mb-1">
          {{ $tt('共找到 {count} 个设备', { count: devices.length }) }}
        </div>

        <div class="device-list-container">
          <li v-for="device in devices" :key="device.udn">
            <a @click="selectDevice(device)" :class="{ 'active': selectedDevice && selectedDevice.udn === device.udn }">
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center min-w-0 flex-1">
                  <svg class="w-4 h-4 fill-current mr-2 flex-shrink-0" viewBox="0 0 1024 1024">
                    <path
                      d="M832 64H192c-17.7 0-32 14.3-32 32v832c0 17.7 14.3 32 32 32h640c17.7 0 32-14.3 32-32V96c0-17.7-14.3-32-32-32z m-40 824H232V136h560v752z" />
                    <path d="M296 224h432v112H296z m0 200h432v112H296z m0 200h432v112H296z" />
                  </svg>
                  <div class="min-w-0 flex-1 w-full">
                    <div class="font-medium truncate w-40">{{ device.device_info.name }}</div>
                    <div class="text-xs text-gray-500 truncate w-40">{{ device.device_info.model_name }}</div>
                  </div>
                </div>
                <div class="flex items-center ml-2">
                  <div :class="connectionStatusClass(device)" class="w-2 h-2 rounded-full"></div>
                  <!-- 显示手动添加设备的标识 -->
                  <svg v-if="device.isManual" class="w-3 h-3 ml-1 fill-current text-blue-500" viewBox="0 0 1024 1024"
                    :title="$tt('手动添加的设备')">
                    <path
                      d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64z m192 472c0 4.4-3.6 8-8 8H544v152c0 4.4-3.6 8-8 8h-48c-4.4 0-8-3.6-8-8V544H328c-4.4 0-8-3.6-8-8v-48c0-4.4 3.6-8 8-8h152V328c0-4.4 3.6-8 8-8h48c4.4 0 8 3.6 8 8v152h152c4.4 0 8 3.6 8 8v48z" />
                  </svg>
                </div>
              </div>
            </a>
          </li>
        </div>
      </ul>
    </div>

    <!-- 手动添加设备模态框 - DaisyUI Modal -->
    <div class="modal" :class="{ 'modal-open': showAddDeviceModal }" @keydown.esc="closeAddDeviceModal">
      <div class="modal-box w-full max-w-md relative">
        <!-- 关闭按钮 -->
        <button @click="closeAddDeviceModal" class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
          ✕
        </button>

        <!-- 模态框头部 -->
        <div class="mb-6">
          <h3 class="text-lg font-bold">{{ $tt('手动添加DLNA设备') }}</h3>
          <p class="text-sm text-base-content/70 mt-1">{{ $tt('填写DLNA设备的基本信息来手动添加设备') }}</p>
        </div>

        <!-- 模态框内容 -->
        <form @submit.prevent="addManualDevice">
          <!-- UDN字段 -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $tt('设备UDN') }} <span class="text-error">*</span></span>
            </label>
            <input v-model="deviceForm.udn" type="text" required
              :placeholder="$tt('例如: uuid:12345678-1234-1234-1234-123456789012')" class="input input-bordered w-full"
              :class="{ 'input-error': errors.udn }" />
            <label v-if="errors.udn" class="label">
              <span class="label-text-alt text-error">{{ errors.udn }}</span>
            </label>
          </div>

          <!-- Description URL字段 -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $tt('描述URL') }} <span class="text-error">*</span></span>
            </label>
            <input v-model="deviceForm.description_url" type="url" required
              :placeholder="$tt('例如: http://192.168.1.100:8200/description.xml')" class="input input-bordered w-full"
              :class="{ 'input-error': errors.description_url }" />
            <label v-if="errors.description_url" class="label">
              <span class="label-text-alt text-error">{{ errors.description_url }}</span>
            </label>
          </div>

          <!-- 友好名称字段 -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $tt('设备名称') }} <span class="text-error">*</span></span>
            </label>
            <input v-model="deviceForm.friendly_name" type="text" required :placeholder="$tt('例如: 客厅音响')"
              class="input input-bordered w-full" :class="{ 'input-error': errors.friendly_name }" />
            <label v-if="errors.friendly_name" class="label">
              <span class="label-text-alt text-error">{{ errors.friendly_name }}</span>
            </label>
          </div>

          <!-- 设备型号字段 -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $tt('设备型号') }}</span>
            </label>
            <input v-model="deviceForm.model_name" type="text" :placeholder="$tt('例如: Smart Speaker Pro')"
              class="input input-bordered w-full" />
          </div>

          <!-- 制造商字段 -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ $tt('制造商') }}</span>
            </label>
            <input v-model="deviceForm.manufacturer" type="text" :placeholder="$tt('例如: Samsung Electronics')"
              class="input input-bordered w-full" />
          </div>

          <!-- 按钮组 -->
          <div class="modal-action">
            <button type="button" @click="closeAddDeviceModal" class="btn btn-ghost">
              {{ $tt('取消') }}
            </button>
            <button type="submit" :disabled="isAddingDevice" class="btn btn-primary"
              :class="{ 'loading': isAddingDevice }">
              <span v-if="!isAddingDevice">{{ $tt('添加设备') }}</span>
              <span v-else>{{ $tt('添加中...') }}</span>
            </button>
          </div>
        </form>
      </div>
      <!-- 点击背景关闭模态框 -->
      <div class="modal-backdrop" @click="closeAddDeviceModal"></div>
    </div>
  </div>
</template>

<script>
import SvgIcon from '@/components/SvgIcon.vue'
export default {
  name: 'DLNADeviceSelector',
  components: {
    SvgIcon
  },
  data() {
    return {
      isRefreshing: false,
      // 手动添加设备相关状态
      showAddDeviceModal: false,
      isAddingDevice: false,
      deviceForm: {
        udn: '',
        description_url: '',
        friendly_name: '',
        model_name: '',
        manufacturer: ''
      },
      errors: {}
    }
  },
  computed: {
    // 从Vuex store获取设备信息
    devices() {
      return this.$store.getters.availableDlnaDevices || []
    },

    selectedDevice() {
      return this.$store.getters.currentDlnaDevice
    },

    isDlnaConnected() {
      return this.$store.getters.isDlnaConnected
    },

    // 动态计算设备列表的高度
    deviceListHeight() {
      const deviceCount = this.devices.length
      if (deviceCount === 0) return 'auto'

      // 每个设备项高度约48px，最多显示8个设备
      const maxVisibleDevices = 8
      const deviceHeight = 48
      const baseHeight = 160 // 本机播放 + 刷新按钮 + 手动添加按钮 + 分隔线等的高度

      if (deviceCount <= 4) {
        return 'auto' // 少于4个设备时不限制高度
      } else if (deviceCount <= maxVisibleDevices) {
        return `${baseHeight + deviceCount * deviceHeight}px`
      } else {
        return `${baseHeight + maxVisibleDevices * deviceHeight}px`
      }
    },

    // 是否显示设备计数提示
    showDeviceCount() {
      return this.devices.length > 6
    },

    connectionStatusClass() {
      return (device) => {
        if (this.selectedDevice && this.selectedDevice.udn === device.udn && this.isDlnaConnected) {
          return 'bg-green-500'
        }
        switch (device.player.state) {
          case 'connected':
            return 'bg-green-500'
          case 'idle':
            return 'bg-yellow-500'
          case 'playing':
            return 'bg-blue-500'
          case 'paused':
            return 'bg-gray-500'
          case 'stopped':
            return 'bg-red-500'
          default:
            return 'bg-gray-500'
        }
      }
    }
  },
  mounted() {
    this.refreshDevices()
  },
  methods: {
    async refreshDevices(refresh = false) {
      console.log('刷新DLNA设备, refresh:', refresh)
      this.isRefreshing = true
      try {
        // 通过Vuex store发现设备
        await this.$store.dispatch('discoverDlnaDevices', refresh)
      } catch (error) {
        console.error('刷新设备失败:', error)
        this.$myMsg.notify({
          title: this.$tt('刷新设备失败！'),
          content: error.message || this.$tt('网络错误'),
          type: 'error',
          time: 3000,
        });
      } finally {
        this.isRefreshing = false
      }
    },

    selectDevice(device) {
      console.log('选择设备:', device)

      // 通过事件通知父组件
      this.$emit('device-selected', device)
    },

    /**
     * 显示添加设备模态框
     */
    showAddDeviceModalFunc() {
      this.showAddDeviceModal = true
      this.resetForm()
    },

    /**
     * 关闭添加设备模态框
     */
    closeAddDeviceModal() {
      this.showAddDeviceModal = false
      this.resetForm()
    },

    /**
     * 重置表单数据
     */
    resetForm() {
      this.deviceForm = {
        udn: '',
        description_url: '',
        friendly_name: '',
        model_name: '',
        manufacturer: ''
      }
      this.errors = {}
    },

    /**
     * 验证表单数据
     * @returns {boolean} 验证是否通过
     */
    validateForm() {
      this.errors = {}
      let isValid = true

      // 验证UDN
      if (!this.deviceForm.udn.trim()) {
        this.errors.udn = this.$tt('设备UDN不能为空')
        isValid = false
      } else if (!this.isValidUDN(this.deviceForm.udn)) {
        this.errors.udn = this.$tt('UDN格式不正确，应为uuid:xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx')
        isValid = false
      }

      // 验证Description URL
      if (!this.deviceForm.description_url.trim()) {
        this.errors.description_url = this.$tt('描述URL不能为空')
        isValid = false
      } else if (!this.isValidURL(this.deviceForm.description_url)) {
        this.errors.description_url = this.$tt('URL格式不正确')
        isValid = false
      }

      // 验证友好名称
      if (!this.deviceForm.friendly_name.trim()) {
        this.errors.friendly_name = this.$tt('设备名称不能为空')
        isValid = false
      } else if (this.deviceForm.friendly_name.length > 100) {
        this.errors.friendly_name = this.$tt('设备名称不能超过100个字符')
        isValid = false
      }

      // 检查UDN是否已存在
      const existingDevice = this.devices.find(device => device.udn === this.deviceForm.udn)
      if (existingDevice) {
        this.errors.udn = this.$tt('该UDN的设备已存在')
        isValid = false
      }

      return isValid
    },

    /**
     * 验证UDN格式
     * @param {string} udn 
     * @returns {boolean}
     */
    isValidUDN(udn) {
      const udnPattern = /^uuid:[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
      return udnPattern.test(udn)
    },

    /**
     * 验证URL格式
     * @param {string} url 
     * @returns {boolean}
     */
    isValidURL(url) {
      try {
        new URL(url)
        return true
      } catch {
        return false
      }
    },

    /**
     * 手动添加DLNA设备
     */
    async addManualDevice() {
      if (!this.validateForm()) {
        return
      }

      this.isAddingDevice = true
      try {
        // 构造设备对象，格式与自动发现的设备保持一致
        const manualDevice = {
          udn: this.deviceForm.udn,
          description_url: this.deviceForm.description_url,
          friendly_name: this.deviceForm.friendly_name,
          model_name: this.deviceForm.model_name || this.$tt('未知型号'),
          manufacturer: this.deviceForm.manufacturer || this.$tt('未知制造商')
        }

        this.$api.dlna.addManualDevice(manualDevice).then(res => {
          this.refreshDevices()
          // 显示成功消息
          this.$myMsg.notify({
            title: this.$tt('添加成功！'),
            content: this.$tt('设备 "{name}" 已添加到设备列表', { name: this.deviceForm.friendly_name }),
            type: 'success',
            time: 3000,
          });
        }).catch(err => {
          console.error('添加设备失败:', err)
        })

        // 关闭模态框
        this.closeAddDeviceModal()

        console.log('手动添加设备成功:', manualDevice)
      } catch (error) {
        console.error('添加设备失败:', error)
        this.$myMsg.notify({
          title: this.$tt('添加设备失败！'),
          content: error.message || this.$tt('未知错误'),
          type: 'error',
          time: 3000,
        });
      } finally {
        this.isAddingDevice = false
      }
    },
  }
}
</script>

<style scoped>
.dlna-device-selector .dropdown-content {
  border: 1px solid hsl(var(--bc) / 0.2);
}

.dlna-device-selector .menu li>a {
  transition: all 0.2s ease;
}

.dlna-device-selector .menu li>a:hover {
  background-color: hsl(var(--bc) / 0.1);
}

.dlna-device-selector .menu li>a.active {
  background-color: hsl(var(--p) / 0.2);
  color: hsl(var(--pc));
}

/* 优化滚动条样式 */
.dlna-device-selector .dropdown-content {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) rgba(243, 244, 246, 0.5);
}

.dlna-device-selector .dropdown-content::-webkit-scrollbar {
  width: 6px;
}

.dlna-device-selector .dropdown-content::-webkit-scrollbar-track {
  background: rgba(243, 244, 246, 0.5);
  border-radius: 3px;
}

.dlna-device-selector .dropdown-content::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
  transition: background 0.2s ease;
}

.dlna-device-selector .dropdown-content::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.8);
}

/* 设备列表容器 */
.device-list-container {
  max-height: calc(60vh - 220px);
  overflow-y: auto;
}

/* 当设备数量较多时的样式优化 */
.dlna-device-selector .menu li>a {
  padding: 8px 12px;
  min-height: 48px;
}

/* 设备名称和型号的文本溢出处理 */
.dlna-device-selector .truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 响应式高度调整 */
@media (max-height: 600px) {
  .device-list-container {
    max-height: 200px;
  }

  .dlna-device-selector .dropdown-content {
    max-height: 300px !important;
  }
}

@media (min-height: 800px) {
  .device-list-container {
    max-height: calc(70vh - 120px);
  }
}

/* 设备状态指示器动画 */
.dlna-device-selector .bg-green-500 {
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

/* DaisyUI 模态框自定义样式 */
.dlna-device-selector .modal {
  backdrop-filter: blur(4px);
}

/* 删除按钮样式优化 */
.dlna-device-selector button[title="删除设备"] {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.dlna-device-selector .menu li:hover button[title="删除设备"] {
  opacity: 1;
}

/* 响应式优化 */
@media (max-width: 640px) {
  .dlna-device-selector .modal-box {
    max-width: 90%;
    margin: 1rem;
  }

  .dlna-device-selector .dropdown-content {
    max-width: 280px;
    right: 0;
  }
}

/* 设备项悬停效果 */
.dlna-device-selector .menu li>a {
  position: relative;
}

.dlna-device-selector .menu li>a:hover .w-3.h-3.ml-1 {
  transform: scale(1.1);
}

/* DaisyUI 模态框表单自定义样式 */
.dlna-device-selector .modal-box .form-control {
  margin-bottom: 1rem;
}

/* 模态框内容区域的样式优化 */
.dlna-device-selector .modal-box {
  max-height: 90vh;
  overflow-y: auto;
}
</style>
